// Manages safe PWA updates: a new service worker is downloaded and put into
// "waiting" state automatically, but it is never activated until the user
// explicitly presses "Apply Update" in Settings — and that button is disabled
// while audio is playing. This directly implements the requirement that the
// app must never unexpectedly reload during a performance.

type Listener = () => void;

export class UpdateManager {
  private registration: ServiceWorkerRegistration | null = null;
  private listeners = new Set<Listener>();
  private updateAvailable = false;

  async register(): Promise<void> {
    if (!('serviceWorker' in navigator)) return;
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      this.registration = registration;

      if (registration.waiting && registration.active) {
        this.updateAvailable = true;
        this.notify();
      }

      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && registration.active) {
            this.updateAvailable = true;
            this.notify();
          }
        });
      });

      // Periodically check for updates in the background (safe: this only
      // downloads and stages a new worker, it never activates it).
      setInterval(() => void registration.update(), 60 * 60 * 1000);

      // 'controllerchange' fires the very FIRST time any service worker
      // takes control of a page too — not just when a new one REPLACES an
      // existing one — which happens on every first-ever visit to this
      // origin in a fresh browser/profile (no previous controller to
      // "change" from, but the event fires anyway). Reloading unconditionally
      // here would violate this file's own stated goal ("never reload
      // during a performance") on literally the first load a user ever
      // does. hadControllerBefore distinguishes a genuine update (SKIP_WAITING
      // was explicitly requested via applyUpdate(), replacing an existing
      // controller) from that harmless first-activation case.
      const hadControllerBefore = navigator.serviceWorker.controller !== null;
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!hadControllerBefore || refreshing) return;
        refreshing = true;
        window.location.reload();
      });
    } catch {
      // Service worker registration failure should not block app usage;
      // the app simply runs without offline-update support in that session.
    }
  }

  isUpdateAvailable(): boolean {
    return this.updateAvailable;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  applyUpdate(): void {
    if (!this.registration?.waiting) return;
    this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }

  // Manually asks the browser to re-fetch sw.js and compare it byte-for-byte
  // against the currently installed worker, instead of waiting for the
  // hourly background check. This only downloads/stages a new worker (same
  // as the background check) — it never activates one, so it's always safe
  // to call regardless of playback state.
  async checkForUpdate(): Promise<void> {
    if (!this.registration) return;
    try {
      await this.registration.update();
    } catch {
      // A failed check (e.g. offline) should not surface as an app error;
      // the periodic background check will retry later.
    }
  }
}

let instance: UpdateManager | null = null;

export function getUpdateManager(): UpdateManager | null {
  if (!('serviceWorker' in navigator)) return null;
  if (!instance) instance = new UpdateManager();
  return instance;
}
